<template>
    <app-layout v-if="isPageReady" class="profile-user">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageTitle">
                    <template #right>
                        <common-button
                            v-if="!isMobileLarge"
                            class="profile-user__button"
                            :label="$t('pages.referrals.show.buttons._back')"
                            type="secondary"
                            size="tiny"
                            @click="$router.go(-1)"
                        ></common-button>
                    </template>
                </common-page-header>
                <template v-if="isMobileLarge">
                    <div class="profile-user__mobile-back">
                        <common-button
                            :label="$t('pages.referrals.show.buttons._back')"
                            type="secondary"
                            size="micro"
                            @click="$router.go(-1)"
                        ></common-button>
                    </div>
                </template>
            </common-content-spacer>

            <common-page-split>
                <template #aside>
                    <common-content-spacer height="medium" :isCenter="true">
                        <network-inlusion-status
                            :targetId="Number(user.id)"
                            targetType="user"
                            :isInMyNetwork="isInMyNetwork"
                            :provider="user"
                            @emitEvent="emitEvent($event)"
                            @added-to-network="memberAddedToNetwork"
                        ></network-inlusion-status>
                        <user-card
                            :physician="user"
                            :showRefCount="false"
                            :showSpecialityCount="true"
                            :bottomText="mainPracticeLocation"
                            :isUserProfileLinkEnabled="false"
                        ></user-card>
                    </common-content-spacer>
                </template>

                <template #main>
                    <common-content-spacer height="medium">
                        <common-summary>
                            <common-summary-item
                                v-if="user.shortBiography"
                                :label="shortBiographyLabel"
                                width="full"
                                :isLabelBolded="true"
                                :showColonSymbol="false"
                            >
                                {{ user.shortBiography }}
                            </common-summary-item>

                            <common-summary-item
                                v-if="yearsInPractice"
                                :label="$t('pages.profile.preview.labels.years_in_practice')"
                                :isLabelBolded="true"
                                :showColonSymbol="false"
                            >
                                {{ yearsInPractice }}
                            </common-summary-item>

                            <common-summary-item
                                v-if="boardCertified"
                                :label="$t('pages.profile.preview.labels.board_certified')"
                                :isLabelBolded="true"
                                :showColonSymbol="false"
                            >
                                {{ boardCertified }}
                            </common-summary-item>

                            <common-summary-item
                                v-if="user.gender"
                                :label="$t('pages.profile.preview.labels.gender')"
                                :isLabelBolded="true"
                                :showColonSymbol="false"
                            >
                                {{ user.gender }}
                            </common-summary-item>

                            <common-summary-item
                                v-if="user.languages"
                                :label="$t('pages.profile.preview.labels.languages')"
                                :isLabelBolded="true"
                                :showColonSymbol="false"
                            >
                                <common-badges-list :listElements="user.languages"></common-badges-list>
                            </common-summary-item>

                            <common-summary-item
                                v-if="user.physicianGroup"
                                :label="$t('pages.profile.preview.labels.physician_group')"
                                :isLabelBolded="true"
                                :showColonSymbol="false"
                            >
                                {{ user.physicianGroup.name }}
                            </common-summary-item>

                            <common-summary-item
                                :label="$t('pages.profile.preview.labels.insurance_plans_accepted')"
                                width="full"
                                :isLabelBolded="true"
                                :showColonSymbol="false"
                            >
                                <common-badges-list :listElements="insurancePlans"></common-badges-list>
                            </common-summary-item>

                            <div v-for="(info, i) of professionalInfo" :key="i" class="professional-info-wrapper">
                                <common-summary-item
                                    v-if="info.data && info.data.length"
                                    :label="info.label"
                                    width="full"
                                    :isLabelBolded="true"
                                    :showColonSymbol="false"
                                >
                                    <span v-for="(option, index) of info.data" :key="index">
                                        <p>{{ formatProfessionalInfo(option) }}</p>
                                    </span>
                                </common-summary-item>
                            </div>
                        </common-summary>
                        <availability-locations
                            :hoverTitle="$t('pages.profile.professional.availability.see')"
                            :userId="user.id"
                            :maxNumberOfItems="3"
                            :locations="practiceLocations"
                            @go-to-location="goToSchedule($event)"
                        ></availability-locations>
                    </common-content-spacer>
                </template>
            </common-page-split>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import { composeDisplayName } from 'components/common/CommonUserName'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import UsersAPI from 'api/UsersAPI'
    import ReferralsAPI from 'api/ReferralsAPI'
    import UserCard from 'components/page-elements/referrals/UserCard'
    import AvailabilityLocations from 'components/page-elements/profile/AvailabilityLocations'
    import NetworkInlusionStatus from 'components/page-elements/network/NetworkInlusionStatus'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, ScreenSizeDetectionMixin],

        components: {
            AppLayout,
            AvailabilityLocations,
            UserCard,
            NetworkInlusionStatus,
        },

        data() {
            return {
                user: {},
                practiceLocations: [],
                insurancePlans: [],
                mainPracticeLocation: '',
                isPageReady: false,
            }
        },

        computed: {
            pageTitle() {
                return this.$t('pages.profile.preview.title', { fullName: this.userNameWithDesignation })
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t(`layouts.app.main_menu.home`),
                        route: { name: 'index.home' },
                    },
                    {
                        label: this.$t('pages.profile.preview.breadcrumbs'),
                    },
                ]
            },

            yearsInPractice() {
                return this.user.specialities[0].practicingSince
                    ? new Date().getFullYear() - this.user.specialities[0].practicingSince
                    : null
            },

            boardCertified() {
                return this.$t(`common.microcopy.${!!this.user.boardCertified}`)
            },

            shortBiographyLabel() {
                return this.$t('pages.profile.preview.labels.short_biography', { fullName: this.userNameWithDesignation })
            },

            isInMyNetwork() {
                return this.user.isInMyNetwork !== undefined ? this.user.isInMyNetwork : true
            },

            userNameWithDesignation() {
                const name = composeDisplayName(this.user)
                return this.user.designation ? `${name}, ${this.user.designation}` : name
            },

            professionalInfo() {
                const { medicalSchools, affiliatedHospitals, residencies, fellowships } = this.user
                return [
                    { data: medicalSchools, label: this.$t('pages.profile.preview.labels.medical_schools') },
                    { data: affiliatedHospitals, label: this.$t('pages.profile.preview.labels.affiliated_hospitals') },
                    { data: residencies, label: this.$t('pages.profile.preview.labels.residencies') },
                    { data: fellowships, label: this.$t('pages.profile.preview.labels.fellowships') },
                ]
            },
        },

        methods: {
            goToSchedule({ practiceLocationId }) {
                ReferralsAPI.getDataForDirectReferral(this.user.id).then((res) => {
                    const query = {
                        userId: res.referredBy,
                        practiceType: res.practice.practiceType,
                        searchBy: 'zipcode',
                        insurancePlanId: res.insurancePlanId || this.insuranceId,
                        specialityId: res.specialityId,
                        subSpecialityId: res.subSpecialityId,
                        zip: res.practice.zip,
                        physicianId: res.referredTo,
                        practiceId: res.practice.id,
                        practiceLocationId,
                    }

                    this.$router.push({
                        name: `referrals.new.make`,
                        query: query,
                    })
                    this.emitEvent({ name: 'location-selected' })
                })
            },

            getUserPracticeLocations(locations) {
                this.practiceLocations = locations.filter((location) => location.isAvailabilitySet)
                if (this.practiceLocations.length) {
                    this.mainPracticeLocation = `${this.practiceLocations[0].city}, ${this.practiceLocations[0].state}`
                }
            },

            getInsurancePlans(insuranceTypes) {
                this.insurancePlans = [...new Set(insuranceTypes.map((type) => type.name))]
            },

            formatProfessionalInfo({ name, city, state }) {
                return `${name}, ${city}, ${state}`
            },

            memberAddedToNetwork() {
                this.user.isInMyNetwork = true
            },

            fetchUserData() {
                const member = this.$auth.getPracticeMember(this.$route.params.id)
                if (member) {
                    this.user = member

                    this.getInsurancePlans(this.user.practice.insurancePlans)
                    this.getUserPracticeLocations(this.user.practice.locations)

                    this.isPageReady = true
                } else {
                    const fields = [
                        'id',
                        'firstName',
                        'lastName',
                        'designation',
                        'email',
                        'userRole(id,name)',
                        'professionalRating',
                        'rating',
                        'isProfileComplete',
                        'specialities(id,speciality(id,name),subSpeciality(id,name),practicingSince)',
                        'boardCertified',
                        'practiceMembershipStatus',
                        'practice(id,name,practiceType,locations(id,name,address,city,state,zip,isAvailabilitySet),insurancePlans(id,name,insurancePlanGroup(id,name)))',
                        'isInMyNetwork',
                        'medicalSchools(id,name,city,state)',
                        'residencies(id,name,city,state)',
                        'affiliatedHospitals(id,name,city,state)',
                        'fellowships(id,name,city,state)',
                        'shortBiography',
                        'gender',
                        'languages',
                        'physicianGroup(id,name)',
                        'avatar(thumb-360,source)',
                    ]

                    UsersAPI.get(this.$route.params.id, this.$auth.getActiveMember().id, fields).then((val) => {
                        this.user = val

                        this.getInsurancePlans(this.user.practice.insurancePlans)
                        this.getUserPracticeLocations(this.user.practice.locations)

                        this.isPageReady = true
                    })
                }
            },
        },

        watch: {
            $route() {
                this.fetchUserData()
            },
        },

        mounted() {
            this.fetchUserData()
        },
    }
</script>
<style lang="scss">
    @import 'styles/setup/_mixins.scss';
    @import 'styles/setup/_variables.scss';

    .profile-user {
        .card {
            min-width: 265px;
            min-height: 400px;

            &__medmatch-rating {
                &--size-large {
                    top: 220px !important;
                }
            }
        }
        .professional-info-wrapper {
            width: 100%;
        }

        &__button {
            @include breakpoint(smartphone-large) {
                position: absolute;
                right: 0px;
                bottom: 40px;
            }
        }
        &__mobile-back {
            display: flex;
            justify-content: flex-end;
        }
    }
</style>
